import { Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import type { StatementDataColumnConfiguration } from "../../../../../../../../api/types/svTypes.ts";
import { selectBuilderDetailedView } from "../../../../store/selectors/fieldsStateSelectors.ts";
import { DEFAULT_AREA_FIELD_ITEM_HEIGHT } from "../../../viewer/common/fields/area-fields/AreaFieldsHelper.ts";
import { isDataColumn } from "../../../viewer/financialStatement/statementColumnLabels.ts";
import CardConditionsButton from "../common/CardConditionsButton.tsx";
import CardConditionsPopover from "../common/CardConditionsPopover.tsx";
import CardDeleteButton from "../common/CardDeleteButton.tsx";
import CardOptionsButton from "../common/CardOptionsButton.tsx";
import DataColumnDatePanel from "./DataColumnDatePanel.tsx";

const getConditionCount = (column: StatementDataColumnConfiguration): number =>
  column.customConditions.length + column.linkedConditions.length;

interface Props {
  column: StatementDataColumnConfiguration;
  label: string;
  hint: string;
  isOnlyDataColumn: boolean;
  removeBlockedMessage?: string;
  optionsOpen: boolean;
  readonly: boolean;
  onShowOptions: (guid: string, anchorEl: HTMLElement) => void;
  onRemove: (guid: string) => void;
}

const DataColumnCard = ({
  column,
  label,
  hint,
  isOnlyDataColumn,
  removeBlockedMessage,
  optionsOpen,
  readonly,
  onShowOptions,
  onRemove,
}: Props) => {
  const [hovered, setHovered] = useState(false);
  const [conditionsAnchorEl, setConditionsAnchorEl] = useState<HTMLElement>();
  const detailedView = useSelector(selectBuilderDetailedView);

  const { guid } = column;
  const hasDatePanel = isDataColumn(column);
  const conditionCount = hasDatePanel ? getConditionCount(column) : 0;
  const showHoverActions = hovered && !readonly;
  const showConditionsButton =
    hasDatePanel && (showHoverActions || conditionCount > 0 || conditionsAnchorEl !== undefined);

  return (
    <Stack
      sx={(theme) => ({
        border: `1px solid ${optionsOpen ? theme.palette.primary.main : theme.palette.divider}`,
        borderRadius: "2px",
        bgcolor: "background.paper",
      })}
    >
      <Stack
        direction="row"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{ minHeight: DEFAULT_AREA_FIELD_ITEM_HEIGHT, alignItems: "center" }}
      >
        <Stack direction="row" sx={{ flex: 1, minWidth: 0, alignItems: "center", gap: 0.75, px: 1.25 }}>
          <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0 }}>
            {label}
          </Typography>
          {!showHoverActions && (
            <Typography variant="caption" noWrap color={isDataColumn(column) ? "primary" : "textSecondary"}>
              {hint}
            </Typography>
          )}
        </Stack>
        {showHoverActions && !isOnlyDataColumn && (
          <CardDeleteButton blockedMessage={removeBlockedMessage} onDelete={() => onRemove(guid)} />
        )}
        {showConditionsButton && (
          <CardConditionsButton
            conditionCount={conditionCount}
            detailedView={detailedView}
            onClick={setConditionsAnchorEl}
          />
        )}
        <CardOptionsButton title="Column Options" onClick={(anchorEl) => onShowOptions(guid, anchorEl)} />
      </Stack>
      {hasDatePanel && detailedView && conditionCount > 0 && (
        <DataColumnDatePanel
          column={column}
          readonly={readonly}
          hideAddActions
          disableAutoShowOptions={conditionsAnchorEl !== undefined}
        />
      )}
      {hasDatePanel && conditionsAnchorEl && (
        <CardConditionsPopover
          anchorEl={conditionsAnchorEl}
          title={label}
          onClose={() => setConditionsAnchorEl(undefined)}
        >
          <DataColumnDatePanel column={column} readonly={readonly} />
        </CardConditionsPopover>
      )}
    </Stack>
  );
};

export default DataColumnCard;
